import { useCallback, useRef } from 'react'
import {
  DEFAULT_FREEZE_S,
  MAX_FREEZE_S,
  MAX_SPEED,
  MIN_CLIP_S,
  MIN_FREEZE_S,
  MIN_SPEED,
  newId,
  nextClipId,
  reorderClips,
  type FreezeFrame,
  type ProjectVideo,
  type ToolProject,
} from '../model'
import { resolvePoint, toSourceTime, videoOffset, type VirtualTimeline } from '../timeline'

export interface ClipMutationsOptions {
  onChange: (updater: (project: ToolProject) => ToolProject) => void
  /** Rebuilt on every project change; read through a ref so the callbacks
   * below stay referentially stable — the timeline's drag listeners
   * re-subscribe on every identity change, and a clip drag fires those
   * dozens of times a second. */
  timeline: VirtualTimeline
  videos: ProjectVideo[]
}

export interface ClipMutations {
  /** Both arguments are **project** seconds; the clip stores source ones. */
  addClip: (startTime: number, endTime: number) => void
  updateBounds: (clipId: number, startTime: number, endTime: number) => void
  renameClip: (clipId: number, name: string) => void
  deleteClip: (clipId: number) => void
  reorder: (from: number, insertAt: number) => void
  /** Keep a clip in the list but out of the cut, or bring it back. */
  toggleHidden: (clipId: number) => void
  setSpeed: (clipId: number, speed: number) => void
  /**
   * Hold the frame under ``time`` (project seconds). Only meaningful in
   * "só os cortes", where a moment belongs to a clip; returns the new
   * freeze's id so the caller can select it, or ``null`` when the playhead
   * isn't over a clip.
   */
  addFreezeAt: (time: number) => string | null
  setFreezeDuration: (clipId: number, freezeId: string, duration: number) => void
  deleteFreeze: (clipId: number, freezeId: string) => void
}

/**
 * Every write to the clip list, in one place.
 *
 * The boundary this hook owns is a coordinate one: the timeline speaks
 * **project** seconds and a ``ProjectClip`` stores seconds inside its own
 * source file. Callers hand over what the user pointed at; the translation,
 * the clamping and the invalidation of stale downloads happen here.
 */
export function useClipMutations({
  onChange,
  timeline,
  videos,
}: ClipMutationsOptions): ClipMutations {
  const timelineRef = useRef(timeline)
  timelineRef.current = timeline
  const videosRef = useRef(videos)
  videosRef.current = videos

  const addClip = useCallback(
    (startTime: number, endTime: number) => {
      const tl = timelineRef.current
      const from = toSourceTime(tl, startTime)
      const to = toSourceTime(tl, endTime)
      if (!from) return
      // A range dragged across a join belongs to the video it started in;
      // the far end is clamped there rather than silently splitting.
      const sameVideo = to && to.videoId === from.videoId
      const video = videosRef.current.find((v) => v.id === from.videoId)
      const end = sameVideo ? to.sourceTime : (video?.duration ?? from.sourceTime + MIN_CLIP_S)
      if (end - from.sourceTime < MIN_CLIP_S) return
      onChange((p) => ({
        ...p,
        clips: [
          ...p.clips,
          {
            id: nextClipId(p),
            videoId: from.videoId,
            start: from.sourceTime,
            end,
            name: '',
            download: null,
            hidden: false,
            speed: 1,
            freezes: [],
          },
        ],
      }))
    },
    [onChange],
  )

  const updateBounds = useCallback(
    (clipId: number, startTime: number, endTime: number) => {
      onChange((p) => {
        const clip = p.clips.find((c) => c.id === clipId)
        if (!clip) return p
        // Dragging is only offered in ``full`` mode, where a video sits at
        // one fixed offset — so undoing the mapping is a subtraction.
        const offset = videoOffset(timelineRef.current, clip.videoId)
        if (offset === null) return p
        const start = Math.max(0, startTime - offset)
        const end = Math.max(start + MIN_CLIP_S, endTime - offset)
        return {
          ...p,
          // New bounds → whatever was exported for this clip points at the
          // wrong footage, so the download link goes away until re-export.
          // Freezes outside the new bounds go with it: a held frame the clip
          // no longer contains has no frame to hold.
          clips: p.clips.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  start,
                  end,
                  download: null,
                  freezes: c.freezes.filter((f) => f.at >= start && f.at <= end),
                }
              : c,
          ),
        }
      })
    },
    [onChange],
  )

  const renameClip = useCallback(
    (clipId: number, name: string) => {
      onChange((p) => ({
        ...p,
        clips: p.clips.map((c) => (c.id === clipId ? { ...c, name } : c)),
      }))
    },
    [onChange],
  )

  const deleteClip = useCallback(
    (clipId: number) => {
      onChange((p) => ({ ...p, clips: p.clips.filter((c) => c.id !== clipId) }))
    },
    [onChange],
  )

  const reorder = useCallback(
    (from: number, insertAt: number) => {
      onChange((p) => ({ ...p, clips: reorderClips(p.clips, from, insertAt) }))
    },
    [onChange],
  )

  const toggleHidden = useCallback(
    (clipId: number) => {
      onChange((p) => ({
        ...p,
        clips: p.clips.map((c) => (c.id === clipId ? { ...c, hidden: !c.hidden } : c)),
      }))
    },
    [onChange],
  )

  const setSpeed = useCallback(
    (clipId: number, speed: number) => {
      const clamped = Math.max(MIN_SPEED, Math.min(MAX_SPEED, speed))
      onChange((p) => ({
        ...p,
        clips: p.clips.map((c) =>
          // A re-timed clip's exported file is the old rate, so it goes the
          // way it does on a bounds change.
          c.id === clipId ? { ...c, speed: clamped, download: null } : c,
        ),
      }))
    },
    [onChange],
  )

  const addFreezeAt = useCallback(
    (time: number): string | null => {
      const point = resolvePoint(timelineRef.current, time)
      if (!point?.segment.clipId) return null
      const clipId = point.segment.clipId
      const at = point.sourceTime
      const id = newId('frz')
      onChange((p) => ({
        ...p,
        clips: p.clips.map((c) => {
          if (c.id !== clipId) return c
          // Two freezes on the same frame would stack invisibly; extending
          // the one that's there is what the user is asking for anyway.
          const existing = c.freezes.find((f) => Math.abs(f.at - at) < 0.02)
          if (existing) {
            return {
              ...c,
              download: null,
              freezes: c.freezes.map((f) =>
                f.id === existing.id
                  ? { ...f, duration: Math.min(MAX_FREEZE_S, f.duration + DEFAULT_FREEZE_S) }
                  : f,
              ),
            }
          }
          const freeze: FreezeFrame = { id, at, duration: DEFAULT_FREEZE_S }
          return { ...c, download: null, freezes: [...c.freezes, freeze] }
        }),
      }))
      return id
    },
    [onChange],
  )

  const setFreezeDuration = useCallback(
    (clipId: number, freezeId: string, duration: number) => {
      const clamped = Math.max(MIN_FREEZE_S, Math.min(MAX_FREEZE_S, duration))
      onChange((p) => ({
        ...p,
        clips: p.clips.map((c) =>
          c.id === clipId
            ? {
                ...c,
                download: null,
                freezes: c.freezes.map((f) => (f.id === freezeId ? { ...f, duration: clamped } : f)),
              }
            : c,
        ),
      }))
    },
    [onChange],
  )

  const deleteFreeze = useCallback(
    (clipId: number, freezeId: string) => {
      onChange((p) => ({
        ...p,
        clips: p.clips.map((c) =>
          c.id === clipId
            ? { ...c, download: null, freezes: c.freezes.filter((f) => f.id !== freezeId) }
            : c,
        ),
      }))
    },
    [onChange],
  )

  return {
    addClip,
    updateBounds,
    renameClip,
    deleteClip,
    reorder,
    toggleHidden,
    setSpeed,
    addFreezeAt,
    setFreezeDuration,
    deleteFreeze,
  }
}
